import React from 'react';
import styled from 'styled-components';
import DotLoader from '@svg/dot_loader';
import CubeIcon from '@svg/cube';

import { Close, DeviceHubOutlined, FiberManualRecord } from '@mui/icons-material';
import { CircularProgress, IconButton, Stack, alpha } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { State, flowActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { Tab as TabType, tabActions } from '@store/slices/tabs';
import { useTheme } from '../../styles';

const Container = styled.div<{ active?: boolean; disabled?: boolean }>`
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;

  font-weight: 500;
  font-size: 14px;

  color: ${({ active, theme, disabled }) =>
    active ? theme.primary : disabled ? theme.text.subtext : theme.text.standard};
  background-color: ${({ active, theme }) => (active ? alpha(theme.primary, 0.06) : 'transparent')};

  border: 0;
  border-right: 1px solid ${({ theme }) => theme.line.light};
  height: 59px;
  padding: 0rem 1rem;
  min-width: 185px;
  max-width: 185px;

  #label {
    max-width: 150px;
    min-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
  }

  #type {
    max-width: 150px;
    min-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.text.subtext};
    font-size: 11px;
  }

  .MuiIconButton-root {
    padding: 2px;
  }

  svg {
    font-size: 1.4ch;
  }

  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 1px;
    background-color: ${({ active, theme }) => (active ? theme.primary : theme.background.standard)};
  }

  &:hover {
    cursor: ${({ disabled, active }) => (disabled && !active ? 'not-allowed' : 'pointer')};
    background-color: ${({ theme, disabled }) => !disabled && alpha(theme.primary, 0.08)};

    &:after {
      display: ${({ active }) => (active ? 'unset' : 'none')};
    }
  }
`;

const Actions = styled.div<{ saved: boolean }>`
  display: flex;
  align-items: center;

  &:hover {
    .MuiIconButton-root {
      &:hover {
        #unsavedTab {
          display: none;
        }

        #savedTab {
          display: block;
        }
      }
    }

    #savedTab {
      display: ${({ saved }) => (saved ? 'block' : 'none')};
    }

    #unsavedTab {
      display: ${({ saved }) => (saved ? 'none' : 'block')};
    }
  }
`;

interface Props {
  label: string;
  tab_flow_id: string;
}

export const findNeighbourTab = (tabList: TabType[], tab_flow_id: string, last_menu: string | null) => {
  const index = tabList.findIndex((tab) => tab.flow_id === tab_flow_id);

  if (index === undefined || index === null) return ['workflow', null, null];
  if (tabList.length === 1 && last_menu) return ['last', null, null];

  if (index === 0 && tabList.length > index + 1)
    return [tabList[index + 1]?.flow_id, tabList[index + 1]?.service_path, tabList[index + 1]?.label];
  if (index !== 0 && index - 1 >= 0)
    return [tabList[index - 1]?.flow_id, tabList[index - 1]?.service_path, tabList[index - 1]?.label];
  if (index !== 0 && tabList.length >= index + 1)
    return [tabList[index + 1]?.flow_id, tabList[index + 1]?.service_path, tabList[index + 1]?.label];

  return ['workflow', null, null];
};

export const Tab: React.FC<Props> = ({ label, tab_flow_id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();

  const tabs = useSelector((state: State) => state.tabs.list);
  const flow_id = useSelector((state: State) => state.tabs.active.flow_id);
  const last_menu = useSelector((state: State) => state.tabs.last_menu);
  const activeTab = useSelector((state: State) => state.tabs.active);
  const loading = activeTab?.loading && tab_flow_id === activeTab?.flow_id;

  const commands = tabs
    .filter((tab) => tab.flow_id === tab_flow_id)
    .map((tab) => tab.commands)
    .flat(10);

  const { service_id, workflow_id } = useParams();

  const trace = useSelector((state: State) => state.tabs.active.run);

  const active = Boolean(location?.pathname?.includes('/workflow/') && flow_id === tab_flow_id && workflow_id);
  const trigger = commands.find((command) => command.config.trigger);
  const saved = Boolean(tabs.find((tab) => tab.flow_id === tab_flow_id)?.saved);

  const setActive = () => {
    if (active || trace?.running) return;

    dispatch(tabActions.setIdentifier(tab_flow_id));
    navigate(`/${service_id}/workflow/${tab_flow_id}`);
  };

  const closeTab = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (saved) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [n_flow_id, n_path, n_tab_name] = findNeighbourTab(tabs, tab_flow_id, last_menu);

      if (tab_flow_id !== workflow_id) {
        dispatch(tabActions.removeTab(tab_flow_id || ''));
        return;
      }

      if (n_flow_id === 'last' && last_menu) {
        navigate(last_menu || `/${service_id}/workflow`);
        dispatch(tabActions.removeTab(tab_flow_id || ''));
        return;
      }

      if (n_flow_id === 'workflow') {
        navigate(last_menu || `/${service_id}/workflow`);
        dispatch(tabActions.removeTab(tab_flow_id || ''));
        return;
      }

      if (n_flow_id !== 'workflow' && n_flow_id && n_tab_name) {
        dispatch(tabActions.setIdentifier(n_flow_id));
        navigate(`/${service_id}/workflow/${n_flow_id}`);
      }

      dispatch(tabActions.removeTab(tab_flow_id || ''));
    } else {
      dispatch(flowActions.setCloseTab(tab_flow_id));
    }
  };

  return (
    <Container title={label} active={active} onClick={setActive} disabled={trace?.running}>
      <Stack spacing={0.5}>
        <div id='label'>{label}</div>
        {trigger && (
          <Stack direction='row' spacing={0.5}>
            {trigger.config.command_id !== 'workflow.module' ? (
              <DeviceHubOutlined sx={{ color: theme.text.subtext, fontSize: '1.4ch !important' }} />
            ) : (
              <CubeIcon color={theme.text.subtext} height={12} />
            )}
            <div id='type'>{trigger.config.command_id !== 'workflow.module' ? 'Workflow' : 'Module'}</div>
          </Stack>
        )}
      </Stack>
      <Actions saved={saved}>
        {trace?.running && active ? (
          <DotLoader height={4} />
        ) : (
          <IconButton sx={{ borderRadius: '5px' }} onClick={closeTab} disabled={trace?.running}>
            <CircularProgress id='loadingTab' sx={{ display: loading ? 'block' : 'none' }} size={12} />
            <Close id='savedTab' sx={{ display: saved && !loading ? 'block' : 'none' }} />
            <FiberManualRecord id='unsavedTab' sx={{ display: saved || loading ? 'none' : 'block' }} />
          </IconButton>
        )}
      </Actions>
    </Container>
  );
};

export default Tab;
